/**
 * Shared helpers for canteen paid/unpaid/advance ledger reports (date-range, by class).
 */

import {
  normalizeCanteenRecordDateString,
  canteenEntryIsPaid,
  canteenEntryIsAdvance,
  getCanteenEntryPaymentDate,
  getCanteenEntryServiceDate,
  listDatesInclusive,
  getCanteenLedgerRowForDate,
  isSettledCanteenStatus,
} from './canteenLedger';

export {
  normalizeCanteenRecordDateString,
  canteenEntryIsPaid,
  canteenEntryIsAdvance,
} from './canteenLedger';

/**
 * @param {{ canteen?: object[], class_?: string, lastname?: string, othernames?: string, ID?: string, _id?: string }[]} studentList
 * @param {string} classFilter - class name or 'none' / empty for all
 * @param {string} startStr - YYYY-MM-DD
 * @param {string} endStr - YYYY-MM-DD
 */
export function computeCanteenPeriodReport(studentList, classFilter, startStr, endStr) {
  const paidLines = [];
  const unpaidLines = [];
  const advanceLines = [];
  const cashCollectedLines = [];
  let totalPaid = 0;
  let totalUnpaid = 0;
  let totalAdvance = 0;
  let totalCashCollected = 0;
  const paidStudentIds = new Set();
  const unpaidStudentIds = new Set();
  const advanceStudentIds = new Set();

  const start = normalizeCanteenRecordDateString(startStr);
  const end = normalizeCanteenRecordDateString(endStr);

  if (!start || !end || start > end) {
    return {
      paidLines,
      unpaidLines,
      advanceLines,
      cashCollectedLines,
      totalPaid,
      totalUnpaid,
      totalAdvance,
      totalCashCollected,
      studentCountPaid: 0,
      studentCountUnpaid: 0,
      studentCountAdvance: 0,
    };
  }

  const periodDates = listDatesInclusive(start, end);
  const matchClass =
    classFilter && classFilter !== 'none' && String(classFilter).trim() !== ''
      ? String(classFilter).trim()
      : null;

  const cashLineKeys = new Set();
  const advanceLineKeys = new Set();

  for (const s of studentList || []) {
    if (matchClass && String(s?.class_ || '').trim() !== matchClass) continue;

    const name = `${s?.lastname || ''} ${s?.othernames || ''}`.trim();
    const studId = s?.ID != null ? String(s.ID) : '';
    const uid = s?._id != null ? String(s._id) : '';
    const canteen = Array.isArray(s?.canteen) ? s.canteen : [];

    for (const entry of canteen) {
      const serviceDate = getCanteenEntryServiceDate(entry);
      const paymentDate = getCanteenEntryPaymentDate(entry);
      const amount = parseFloat(entry.amount) || 0;
      if (amount <= 0) continue;

      const baseLine = {
        studentName: name,
        studentSchoolId: studId,
        className: s?.class_ || '',
        date: serviceDate,
        paymentDate: paymentDate || serviceDate,
        amount,
        status: entry.status || '',
        description: entry.description || '',
      };

      if (
        paymentDate &&
        paymentDate >= start &&
        paymentDate <= end &&
        canteenEntryIsPaid(entry)
      ) {
        const cashKey = `${uid}|${paymentDate}|${serviceDate}|${amount}`;
        if (!cashLineKeys.has(cashKey)) {
          cashLineKeys.add(cashKey);
          cashCollectedLines.push(baseLine);
          totalCashCollected += amount;
        }
      }

      if (
        canteenEntryIsAdvance(entry) &&
        paymentDate &&
        paymentDate >= start &&
        paymentDate <= end
      ) {
        const advKey = `${uid}|${paymentDate}|${serviceDate}`;
        if (!advanceLineKeys.has(advKey)) {
          advanceLineKeys.add(advKey);
          advanceLines.push({ ...baseLine, isAdvance: true });
          totalAdvance += amount;
          if (uid) advanceStudentIds.add(uid);
        }
      }
    }

    for (const serviceDate of periodDates) {
      const ledger = getCanteenLedgerRowForDate(s, serviceDate);
      const entry = ledger.entry;
      const amount =
        entry != null
          ? parseFloat(entry.amount) || 0
          : 0;

      const baseLine = {
        studentName: name,
        studentSchoolId: studId,
        className: s?.class_ || '',
        date: serviceDate,
        paymentDate: ledger.paymentDate || serviceDate,
        amount,
        status: ledger.status,
        description: entry?.description || '',
      };

      if (isSettledCanteenStatus(ledger.status)) {
        if (ledger.status === 'Advance') {
          paidLines.push({ ...baseLine, isAdvance: true });
        } else {
          paidLines.push(baseLine);
        }
        totalPaid += amount;
        if (uid) paidStudentIds.add(uid);
      } else {
        const paidPart =
          entry != null && Number.isFinite(parseFloat(entry.paid))
            ? parseFloat(entry.paid)
            : 0;
        const owing =
          amount > 0 ? Math.max(0, amount - paidPart) : 0;

        unpaidLines.push({
          ...baseLine,
          amount: owing,
          noRecord: !entry,
          description:
            !entry
              ? 'No payment recorded'
              : entry.description ||
                (ledger.status === 'Unpaid' ? 'Unpaid' : 'No payment recorded'),
        });
        totalUnpaid += owing;
        if (uid) unpaidStudentIds.add(uid);
      }
    }
  }

  const byDateThenName = (a, b) =>
    a.date.localeCompare(b.date) ||
    a.studentName.localeCompare(b.studentName);

  paidLines.sort(byDateThenName);
  unpaidLines.sort(byDateThenName);

  return {
    paidLines,
    unpaidLines,
    advanceLines,
    cashCollectedLines,
    totalPaid,
    totalUnpaid,
    totalAdvance,
    totalCashCollected,
    studentCountPaid: paidStudentIds.size,
    studentCountUnpaid: unpaidStudentIds.size,
    studentCountAdvance: advanceStudentIds.size,
  };
}
